import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
  Typography,
} from '@mui/joy'
import { Warning } from '@mui/icons-material'
import { getAutocompleteUsers } from 'features/autocompleteSlice'
import { CUSTOM_ERROR_CODES } from 'utils/config'
import manualAssignUserSchema from 'validations/task/manualAssignUserSchema'
import CustomAutocompleteControl from './forms/CustomAutocompleteControl'
import CustomRadioControl from './forms/CustomRadioControl'

const AssignUserModal = ({ open, setOpen, onClose, onSubmit, loading, forcedMode, setForcedMode }) => {
  const dispatch = useDispatch()

  const { usersList, loading: autocompleteLoading, error: autocompleteError } = useSelector((s) => s.autocomplete)
  const { currentProject } = useSelector((s) => s.projects)

  const formik = useFormik({
    initialValues: { user: null, scope: [2, 3].includes(currentProject.type) ? 'project' : 'all' },
    validationSchema: manualAssignUserSchema,
    onSubmit: (values) => {
      const { user } = values
      if (user.id) onSubmit(user.id)
    },
  })

  useEffect(() => {
    if (!open) return
    formik.setFieldValue('user', null, false)
    formik.setFieldTouched('user', false, false)
    if (formik.values.scope === 'project')
      dispatch(getAutocompleteUsers({ projectId: currentProject.id, taskDetails: true }))
    else dispatch(getAutocompleteUsers({ taskDetails: true }))
  }, [open, formik.values.scope])

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog variant="outlined" role="alertdialog" sx={{ width: '100%', maxWidth: '500px' }}>
        <DialogTitle>Ręczne przypisanie użytkownika</DialogTitle>
        <Divider />
        {forcedMode && forcedMode == CUSTOM_ERROR_CODES.TASK_ALREADY_ASSIGNED_SAME_DAY && (
          <Alert color="warning" variant="soft">
            Uwaga! Kolejna próba przypisania zignoruje ograniczenie maksymalnie jednego zadania dziennie.
          </Alert>
        )}
        {forcedMode && forcedMode == CUSTOM_ERROR_CODES.BILLING_INFO_INCOMPLETE && (
          <Alert color="warning" variant="soft">
            Uwaga! Kolejna próba przypisania zignoruje ograniczenie braku uzupełnienia danych do umowy.
          </Alert>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            {autocompleteError && (
              <Alert color="danger" variant="soft" startDecorator={<Warning />}>
                {autocompleteError}
              </Alert>
            )}
            {usersList && (
              <DialogContent sx={{ overflow: 'visible' }}>
                {[2, 3].includes(currentProject.type) && (
                  <CustomRadioControl
                    sx={{ marginBottom: 1 }}
                    name="scope"
                    label={<strong>Użytkownicy:</strong>}
                    options={[
                      { label: 'Przypisani do projektu', value: 'project' },
                      { label: 'Pełna lista', value: 'all' },
                    ]}
                    formik={formik}
                    orientation="horizontal"
                  />
                )}
                <CustomAutocompleteControl
                  loading={autocompleteLoading}
                  name="user"
                  label={
                    <Box sx={{ display: 'flex', gap: 0.5, width: '100%' }}>
                      <Typography level="inherit" sx={{ marginRight: 'auto' }}>
                        Szukaj użytkownika
                      </Typography>
                      <Typography level="inherit" color="warning">
                        w trakcie
                      </Typography>
                      <Typography level="inherit">/</Typography>
                      <Typography level="inherit" color="success">
                        wszytkie
                      </Typography>
                    </Box>
                  }
                  options={usersList}
                  formik={formik}
                  onSelect={() => setForcedMode(false)}
                  sx={{ marginBottom: 2 }}
                  customRenderOption={(opt) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                      <Typography level="inherit" sx={{ marginRight: 'auto' }}>
                        {opt.label}
                      </Typography>
                      <Typography level="title-sm" color="warning">
                        {opt.tasksInProgress}
                      </Typography>
                      <Typography level="title-sm">/</Typography>
                      <Typography level="title-sm" color="success">
                        {opt.tasksInProgress + opt.tasksDone}
                      </Typography>
                    </Box>
                  )}
                />
                <DialogActions>
                  <Button type="submit" variant="solid" color="danger" loading={loading || autocompleteLoading}>
                    {forcedMode &&
                      forcedMode == CUSTOM_ERROR_CODES.TASK_ALREADY_ASSIGNED_SAME_DAY &&
                      'Przypisz igrnoując limit'}
                    {forcedMode &&
                      forcedMode == CUSTOM_ERROR_CODES.BILLING_INFO_INCOMPLETE &&
                      'Przypisz igrnoując brak danych'}
                    {!forcedMode && 'Przypisz'}
                  </Button>
                  <Button
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      setOpen(false)
                      setForcedMode(false)
                      formik.resetForm()
                    }}
                    loading={loading || autocompleteLoading}
                  >
                    Anuluj
                  </Button>
                </DialogActions>
              </DialogContent>
            )}
          </form>
        </FormikProvider>
      </ModalDialog>
    </Modal>
  )
}

AssignUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  forcedMode: PropTypes.bool,
  setForcedMode: PropTypes.func,
  onClose: PropTypes.func,
}

export default AssignUserModal
